"use client";

import { FormEvent, useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  ShieldCheck,
  Smartphone
} from "lucide-react";
import logoUrl from "./logo/thh logo.jpg";

const logoSrc =
  typeof logoUrl === "string"
    ? logoUrl
    : (logoUrl as unknown as { src: string }).src;

const errorCopy: Record<string, string> = {
  LOGIN_INVALID: "Kullanıcı adı veya şifre doğru değil.",
  LOGIN_RATE_LIMITED:
    "Art arda çok sayıda deneme yapıldı. 15 dakika sonra tekrar deneyin.",
  LOGIN_NOT_CONFIGURED:
    "Portal girişi henüz hazırlanmadı. Sistem yöneticisine haber verin.",
  LOGIN_ORIGIN_INVALID:
    "Giriş isteği güvenlik kontrolünden geçemedi. Sayfayı yenileyin."
};

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Giriş · The Heal Hub - Portal";
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (working) return;
    setWorking(true);
    setError("");

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      const result = (await response.json().catch(() => null)) as {
        error?: string;
      } | null;
      if (!response.ok) {
        setError(
          errorCopy[result?.error ?? ""] ??
            "Giriş tamamlanamadı. Bağlantıyı kontrol edip tekrar deneyin."
        );
        return;
      }
      window.location.replace("/");
    } catch {
      setError(
        "Portala şu anda ulaşılamıyor. İnternet bağlantısını kontrol edin."
      );
    } finally {
      setWorking(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-brand-panel" aria-label="The Heal Hub Portal">
        <div className="login-brand-bars" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
        <div className="login-brand-content">
          <span className="login-logo">
            <img src={logoSrc} alt="The Heal Hub" />
          </span>
          <span className="eyebrow">The Heal Hub · Portal</span>
          <h1>Stüdyonun ritmi,<br />tek güvenli kapıdan.</h1>
          <p>
            Klinik masasından, evden veya telefondan aynı güncel çalışma
            alanına ulaşın.
          </p>
        </div>
        <div className="login-trust-strip">
          <span><ShieldCheck size={17} /> Güvenli oturum</span>
          <span><Smartphone size={17} /> Her cihazdan erişim</span>
        </div>
      </section>

      <section className="login-form-panel">
        <div className="login-form-wrap">
          <header>
            <span className="login-lock"><LockKeyhole size={21} /></span>
            <div>
              <span className="eyebrow">Personel erişimi</span>
              <h2>Portala giriş yapın</h2>
              <p>Devam etmek için ortak çalışma hesabını kullanın.</p>
            </div>
          </header>

          <form onSubmit={submit} className="login-form">
            <label>
              <span>Kullanıcı adı</span>
              <input
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                disabled={working}
                required
                autoFocus
              />
            </label>
            <label>
              <span>Şifre</span>
              <div className="login-password">
                <input
                  type={passwordVisible ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={working}
                  required
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible((visible) => !visible)}
                  aria-label={passwordVisible ? "Şifreyi gizle" : "Şifreyi göster"}
                  tabIndex={-1}
                >
                  {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}

            <button className="login-submit" type="submit" disabled={working}>
              <span>{working ? "Giriş kontrol ediliyor…" : "Giriş yap"}</span>
              {working ? <i aria-hidden="true" /> : <ArrowRight size={19} />}
            </button>
          </form>

          <div className="login-session-note">
            <CheckCircle2 size={17} />
            <span>
              Bu cihazdaki oturum güvenli biçimde açık kalır. İşiniz bittiğinde
              profil menüsünden çıkış yapabilirsiniz.
            </span>
          </div>
        </div>
      </section>
    </main>
  );
}
